/**
 * NotificationsBell — campanella in topbar.
 *
 * Mostra il counter dei non letti + un dropdown con le ultime 10 notifiche.
 * Polling automatico ogni 60s (visibile o in background). Quando l'utente
 * clicca su una notifica → marca come letta e naviga al `link` collegato.
 *
 * Design (2026-06-08):
 *  - Stile Facebook/Instagram: notifiche persistenti, badge sul pallino
 *    della campanella, riga sfondata se non letta, click = consumer +
 *    redirect.
 *  - Polling ogni 60s. Refresh anche su `focus` per essere reattivi
 *    quando l'utente torna sulla tab.
 *  - Link "Vedi tutte" porta a `/notifications` per la storia completa.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellOff, CheckCheck, CalendarDays, FileWarning, Wrench, StickyNote, Send } from 'lucide-react';
import { notificationsApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import usePushNotifications from '../lib/usePushNotifications';
import { toast } from 'sonner';

const POLL_INTERVAL_MS = 60_000;

const TYPE_META = {
  leave_request: { icon: CalendarDays, color: 'text-amber-600 bg-amber-50' },
  leave_decided: { icon: CalendarDays, color: 'text-emerald-600 bg-emerald-50' },
  document_expiring: { icon: FileWarning, color: 'text-red-600 bg-red-50' },
  ticket_opened: { icon: Wrench, color: 'text-blue-600 bg-blue-50' },
  store_note: { icon: StickyNote, color: 'text-emerald-600 bg-emerald-50' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diffMs / 60_000);
  if (m < 1) return 'ora';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}g`;
  return new Date(iso).toLocaleDateString('it-IT');
}

export default function NotificationsBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const push = usePushNotifications();

  // Non mostriamo la campanella ai store_user — il loro flow non genera
  // notifiche manageriali e renderebbe la UI rumorosa.
  const hidden = !user || user.role === 'store_user';

  const refreshCount = useCallback(async () => {
    try {
      const { data } = await notificationsApi.unreadCount();
      setCount(data?.count || 0);
    } catch (e) {
      // Silent: campanella non è un errore bloccante
    }
  }, []);

  const refreshList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await notificationsApi.list({ limit: 10 });
      setItems(data || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Polling counter ogni 60s + refresh al focus tab
  useEffect(() => {
    if (hidden) return undefined;
    refreshCount();
    const id = setInterval(refreshCount, POLL_INTERVAL_MS);
    const onFocus = () => refreshCount();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [hidden, refreshCount]);

  // Carica la lista solo quando il dropdown si apre
  useEffect(() => {
    if (open) refreshList();
  }, [open, refreshList]);

  // Click outside per chiudere il dropdown
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleItemClick = async (n) => {
    setOpen(false);
    try {
      if (!n.is_read) {
        await notificationsApi.markRead(n.notification_id);
        setCount((c) => Math.max(0, c - 1));
      }
    } catch (e) {
      // ignora errore di marcatura, naviga comunque
    }
    if (n.link) navigate(n.link);
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setCount(0);
    } catch (e) {
      // silent
    }
  };

  if (hidden) return null;

  return (
    <div className="relative" ref={dropdownRef} data-testid="notifications-bell-wrapper">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 hover:bg-slate-100 transition-colors"
        aria-label={`Notifiche${count > 0 ? ` (${count} non lette)` : ''}`}
        data-testid="notifications-bell-btn"
      >
        <Bell className="w-5 h-5 text-slate-700" />
        {count > 0 && (
          <span
            className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-white"
            data-testid="notifications-bell-badge"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[360px] max-w-[calc(100vw-32px)] bg-white border border-slate-200 shadow-xl z-50"
          data-testid="notifications-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <span className="text-sm font-semibold text-slate-900">Notifiche</span>
            {count > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"
                data-testid="notifications-mark-all-read"
              >
                <CheckCheck className="w-3.5 h-3.5" /> Segna tutte come lette
              </button>
            )}
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {loading && (
              <div className="px-4 py-10 text-center text-xs text-slate-400">Caricamento…</div>
            )}
            {!loading && items.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-slate-400" data-testid="notifications-empty">
                Nessuna notifica.
              </div>
            )}
            {!loading && items.map((n) => {
              const meta = TYPE_META[n.type] || { icon: Bell, color: 'text-slate-600 bg-slate-100' };
              const Icon = meta.icon;
              return (
                <button
                  key={n.notification_id}
                  onClick={() => handleItemClick(n)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors flex gap-3 ${
                    !n.is_read ? 'bg-blue-50/40' : ''
                  }`}
                  data-testid={`notification-item-${n.notification_id}`}
                >
                  <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm leading-snug ${!n.is_read ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                        {n.title}
                      </p>
                      {!n.is_read && (
                        <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />
                      )}
                    </div>
                    {n.body && (
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.body}</p>
                    )}
                    <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">{timeAgo(n.created_at)}</p>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="border-t border-slate-100">
            {/* Toggle Push notifications: visibile solo se browser supporta
                + server configurato. Mostra anche un piccolo "Test" quando
                attive, per consentire all'utente di verificare end-to-end. */}
            {push.supported && push.configured && (
              <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between gap-2 bg-slate-50/60" data-testid="push-toggle-row">
                <div className="flex items-center gap-2 min-w-0">
                  {push.subscribed ? <Bell className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> : <BellOff className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-700 truncate">
                      Notifiche push {push.subscribed ? 'attive' : 'disattive'}
                    </p>
                    {push.permission === 'denied' && (
                      <p className="text-[10px] text-red-600">Permesso negato dal browser</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {push.subscribed && (
                    <button
                      onClick={async () => {
                        const ok = await push.testPush();
                        if (ok) toast.success('Push di test inviata');
                        else toast.error(push.error || 'Errore invio test');
                      }}
                      disabled={push.loading}
                      className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-40 inline-flex items-center gap-1"
                      data-testid="push-test-btn"
                      title="Invia una push di test"
                    >
                      <Send className="w-3 h-3" /> Test
                    </button>
                  )}
                  {push.subscribed ? (
                    <button
                      onClick={async () => {
                        const ok = await push.disable();
                        if (ok) toast.success('Push disattivate');
                        else toast.error(push.error || 'Errore disattivazione');
                      }}
                      disabled={push.loading}
                      className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-40"
                      data-testid="push-disable-btn"
                    >
                      Disattiva
                    </button>
                  ) : (
                    <button
                      onClick={async () => {
                        const ok = await push.enable();
                        if (ok) toast.success('Push attivate');
                        else toast.error(push.error || 'Permesso negato');
                      }}
                      disabled={push.loading || push.permission === 'denied'}
                      className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 bg-black text-white hover:bg-slate-900 disabled:opacity-40"
                      data-testid="push-enable-btn"
                    >
                      Attiva
                    </button>
                  )}
                </div>
              </div>
            )}
            <button
              onClick={() => { setOpen(false); navigate('/notifications'); }}
              className="w-full px-4 py-3 text-xs text-slate-600 hover:bg-slate-50 text-center uppercase tracking-wider font-semibold"
              data-testid="notifications-view-all"
            >
              Vedi tutte le notifiche
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
